<template>
  <!--
    My Orders tab — pulls the session's order list from
    GET /v1/services/{token}/my-order on mount and on manual refresh.

    The API returns ALL items in the session (including cancelled). We
    show them grouped by state with the "happening now" buckets up top
    so the customer can see what's coming next at a glance.

    Decimal fields (`price`, `total`, `cancelled_amount`, `sale_total`)
    arrive as strings — parse before doing math.
  -->
  <div class="px-3 pt-3">

    <!--
      Header summary. During the very first load we swap each dynamic
      value for an inline shimmer bar instead of flashing "Walk-in / 0
      items / ฿0.00" — those placeholder zeros were what made the
      handover feel like a sudden data dump. The tiny fade-swap
      transition eases the real numbers in once they arrive.
    -->
    <div class="card border-0 shadow-sm rounded-3 mb-3 bg-theme text-white">
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="small opacity-75">
            <!-- Skeleton → real value swaps instantly (no transition) so
                 the data feels immediate the moment fetchData resolves;
                 the fade was adding ~240ms of perceived lag. -->
            <span v-if="initialLoading" class="skeleton-shimmer-light rounded d-inline-block align-middle" style="height: 12px; width: 110px;" />
            <span v-else class="d-inline-flex align-items-center flex-wrap" style="gap: 0.5rem;">
              <span v-if="sessionNoDisplay" class="fw-bold">
                #{{ sessionNoDisplay }}
              </span>
              <span v-if="session.table">
                <i class="fa-solid fa-chair me-1" />{{ session.table }}
              </span>
              <span>
                <i class="fa-solid fa-user me-1" />{{ session.name || $t('Walk-in') }}
              </span>
              <span v-if="session.customer_count">
                <i class="fa-solid fa-user-group me-1" />{{ session.customer_count }}
              </span>
            </span>
          </div>
          <button
            class="btn btn-sm btn-light rounded-pill px-3 fw-bold"
            :disabled="loading"
            @click="fetchData(true)"
          >
            <i :class="loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'" />
            <span class="ms-1 d-none d-sm-inline">{{ $t('Refresh') }}</span>
          </button>
        </div>
        <div class="d-flex justify-content-between align-items-end">
          <div>
            <div class="small opacity-75">{{ $t('Items ordered') }}</div>
            <div v-if="initialLoading" class="skeleton-shimmer-light rounded mt-1" style="height: 22px; width: 100px;" />
            <div v-else class="fs-5 fw-bold">{{ totalItems }} {{ $t('items') }}</div>
          </div>
          <div class="text-end">
            <div class="small opacity-75">{{ $t('Current Total') }}</div>
            <div v-if="initialLoading" class="skeleton-shimmer-light rounded mt-1 ms-auto" style="height: 28px; width: 130px;" />
            <div v-else class="fs-4 fw-bold">{{ $storeIndex.formatThaiCurrency(runningTotal) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading skeleton. Includes a group-header strip so the layout
         matches the grouped-by-state view that takes over after data
         lands — no jump from "flat list" to "sectioned list". -->
    <div v-if="initialLoading">
      <div class="skeleton-shimmer rounded mb-2" style="height: 12px; width: 120px;" />
      <div class="d-flex flex-column gap-2 mb-3">
        <div
          v-for="i in 3" :key="'ord-sk-' + i"
          class="card border-0 shadow-sm rounded-3"
        >
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <div class="skeleton-shimmer rounded mb-2" style="height: 16px; width: 55%;" />
                <div class="skeleton-shimmer rounded" style="height: 11px; width: 75%;" />
              </div>
              <div class="text-end">
                <div class="skeleton-shimmer rounded mb-1 ms-auto" style="height: 14px; width: 40px;" />
                <div class="skeleton-shimmer rounded ms-auto" style="height: 10px; width: 55px;" />
              </div>
            </div>
            <div class="mt-2 d-flex justify-content-between">
              <div class="skeleton-shimmer rounded-pill" style="height: 18px; width: 90px;" />
              <div class="skeleton-shimmer rounded" style="height: 12px; width: 60px;" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error state with retry -->
    <div v-else-if="error" class="text-center text-muted py-5">
      <i class="fa-solid fa-circle-exclamation fa-3x text-danger mb-3 opacity-75" />
      <p class="fw-bold mb-1">{{ $t('Failed to load orders') }}</p>
      <p class="small mb-3">{{ error }}</p>
      <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchData(true)">
        <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
      </button>
    </div>

    <!-- Empty state -->
    <div v-else-if="items.length === 0" class="text-center text-muted py-5">
      <i class="fa-solid fa-receipt fa-3x mb-3 opacity-50" />
      <p class="fw-bold">{{ $t('No items yet') }}</p>
      <p class="small">{{ $t('Browse the menu to start your order') }}</p>
      <button class="btn btn-outline-theme rounded-pill px-4" @click="$emit('switch-tab', 'menu')">
        <i class="fa-solid fa-utensils me-2" />{{ $t('Open Menu') }}
      </button>
    </div>

    <!-- Items grouped by state. Each group's items animate in via
         transition-group with a per-row stagger so the list "ripples"
         in rather than slamming down. A flat running index is used so
         the stagger continues across group boundaries. -->
    <div v-else>
      <div
        v-for="(group, gi) in groupedItems"
        :key="group.state"
        class="mb-3"
      >
        <div class="fw-bold text-muted small mb-2 text-uppercase">
          <i :class="stateIcon(group.state)" class="me-1" />
          {{ $t(stateLabel(group.state)) }}
          <span class="badge bg-secondary ms-1">{{ group.items.length }}</span>
        </div>

        <transition-group name="list-stagger" tag="div" appear>
        <div
          v-for="(item, ii) in group.items"
          :key="item.id"
          class="card border-0 shadow-sm rounded-3 mb-2"
          :class="{ 'opacity-50': item.state === 'cancelled' }"
          :style="{ '--enter-delay': (staggerIndex(gi, ii) * 25) + 'ms' }"
        >
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="fw-bold">
                <span v-if="item.queue_no" class="badge bg-theme me-2" style="font-size: 0.7rem;">
                  #{{ item.queue_no }}
                </span>
                {{ $ln(item, 'product_name') }}
              </div>
              <div class="text-end">
                <!-- Quantity: strike-through full qty if cancelled, otherwise show
                     effective qty with a small "(-N)" partial-cancel marker. -->
                <div class="fw-bold">
                  <template v-if="item.state === 'cancelled'">
                    <s class="text-muted">x{{ toInt(item.quantity) }}</s>
                  </template>
                  <template v-else>
                    x{{ effectiveQty(item) }}
                    <small v-if="toInt(item.cancelled_quantity) > 0" class="text-danger ms-1" style="font-size: 0.7rem;">
                      (<s>{{ toInt(item.cancelled_quantity) }}</s>)
                    </small>
                  </template>
                </div>
                <div class="small text-muted">{{ $storeIndex.formatThaiCurrency(item.total) }}</div>
              </div>
            </div>

            <!-- Modifiers, grouped by their `group` label when present -->
            <div v-if="item.modifiers && item.modifiers.length" class="mt-1 ms-1">
              <template v-for="(g, gi) in groupModifiers(item.modifiers)" :key="'g-' + item.id + '-' + gi">
                <div v-if="g.name" class="text-muted fw-semibold" style="font-size: 0.72rem; line-height: 1.8;">
                  {{ $ln(g) }}
                </div>
                <div
                  v-for="mod in g.options"
                  :key="'mod-' + mod.id"
                  class="d-flex align-items-center gap-1 text-muted"
                  style="font-size: 0.78rem; line-height: 1.6;"
                >
                  <i class="fa-solid fa-circle" style="font-size: 0.25rem;" />
                  <span>{{ $t(mod.name) }}</span>
                  <span v-if="parseFloat(mod.price) > 0" class="text-theme fw-semibold">
                    +{{ $storeIndex.formatThaiCurrency(mod.price) }}
                  </span>
                </div>
              </template>
            </div>

            <div v-if="item.remark" class="mt-1">
              <span class="badge bg-light text-dark border small">
                <i class="fa-solid fa-comment-dots me-1 text-theme" />{{ item.remark }}
              </span>
            </div>

            <!-- Partial-cancel reason chip — only when SOME qty was cancelled
                 but the item itself is still active (state !== cancelled). -->
            <div v-if="toInt(item.cancelled_quantity) > 0 && item.cancelled_reason && item.state !== 'cancelled'" class="mt-1">
              <span class="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style="font-size: 0.72rem;">
                <i class="fa-solid fa-ban me-1" />{{ $t(item.cancelled_reason) }}
                <span v-if="parseFloat(item.cancelled_amount) > 0" class="ms-1">
                  (-{{ $storeIndex.formatThaiCurrency(item.cancelled_amount) }})
                </span>
              </span>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-2">
              <span class="badge" :class="stateBadgeClass(item.state)">
                <i :class="stateIcon(item.state)" class="me-1" />{{ $t(stateLabel(item.state)) }}
              </span>
              <span v-if="item.queue_no" class="small text-muted">
                <i class="fa-solid fa-hashtag me-1" />{{ $t('Queue') }} {{ item.queue_no }}
              </span>
            </div>
          </div>
        </div>
        </transition-group>
      </div>
    </div>

  </div>
</template>

<script>
import { ServiceMyOrder, isServiceSessionInvalid } from '@/services/endpoint/services.service';

export default {
  name: 'TabOrders',
  emits: ['switch-tab', 'ready', 'session-invalid'],
  data() {
    return {
      loading: true,
      error: null,
      // Session-level fields surfaced from the API (table name, customer count, etc.)
      session: {
        name: '',
        seat: 0,
        state: '',
        type: '',
        customer_count: 0,
        sale_total: '0.00',
        created_by: '',
      },
      items: [],
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    /** Effective items count excluding cancelled lines + cancelled qty. */
    totalItems() {
      return this.items
        .filter(i => i.state !== 'cancelled')
        .reduce((sum, i) => sum + this.effectiveQty(i), 0);
    },
    /** Running subtotal of items still on the bill (cancelled lines excluded). */
    runningTotal() {
      return this.items
        .filter(i => i.state !== 'cancelled')
        .reduce((sum, i) => sum + parseFloat(i.total || 0), 0);
    },
    groupedItems() {
      // Order from "happening now" → "done" so the latest activity sits up top.
      const order = ['queued', 'progressing', 'ready', 'completed', 'cancelled'];
      const buckets = {};
      for (const item of this.items) {
        const key = item.state || 'queued';
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(item);
      }
      return order
        .filter(s => buckets[s] && buckets[s].length)
        .map(s => ({ state: s, items: buckets[s] }));
    },
    /**
     * First-paint loading — distinguishes "no data yet" from "manual
     * refresh while we already have data on screen" so the latter
     * doesn't blow away the list to flash a skeleton.
     */
    initialLoading() {
      return this.loading && this.items.length === 0;
    },
    sessionNoDisplay() {
      if (!this.session.session_no) return '';
      return String(this.session.session_no).padStart(4, '0');
    },
  },
  methods: {
    async fetchData(silent = false) {
      // `silent` keeps the existing list visible (used by the refresh button)
      // while the network request is in flight — only the spinner inside the
      // refresh button changes. First mount shows the skeleton.
      if (!silent) this.loading = true;
      else this.loading = true;
      this.error = null;
      let sessionInvalid = false;
      try {
        const res = await ServiceMyOrder.get(this.token);
        const data = res.data?.data ?? res.data ?? {};
        this.session = {
          table: data.table || '',
          name: data.name || '',
          session_no: data.session_no,
          seat: data.seat || 0,
          state: data.state || '',
          type: data.type || '',
          customer_count: data.customer_count || 0,
          sale_total: data.sale_total || '0.00',
          created_by: data.created_by || '',
        };
        this.items = Array.isArray(data.items) ? data.items : [];
      } catch (err) {
        if (isServiceSessionInvalid(err)) {
          sessionInvalid = true;
          this.$emit('session-invalid');
          return;
        }
        const msg = err?.response?.data?.message || err?.message || this.$t('Network error');
        this.error = msg;
        if (err?.response?.data?.message) {
          this.$toast?.warn?.(err.response.data.message);
        }
      } finally {
        this.loading = false;
        if (!sessionInvalid) this.$emit('ready');
      }
    },

    /** Best-effort integer parse — quantities sometimes arrive as strings. */
    toInt(v) {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    },
    effectiveQty(item) {
      return Math.max(0, this.toInt(item.quantity) - this.toInt(item.cancelled_quantity));
    },

    /**
     * Modifier rows arrive flat; collapse them into sub-groups by the
     * `group` field so the customer sees them as "Spice level: Mild"
     * rather than a wall of bullet points.
     */
    groupModifiers(modifiers) {
      if (!modifiers || modifiers.length === 0) return [];
      const hasGroups = modifiers.some(m => m.group);
      if (!hasGroups) return [{ name: null, options: modifiers }];
      const groups = {};
      for (const mod of modifiers) {
        const groupName = mod.group || '';
        if (!groups[groupName]) groups[groupName] = { name: groupName || null, name_en: mod.group_en || null, options: [] };
        groups[groupName].options.push(mod);
      }
      return Object.values(groups);
    },

    stateBadgeClass(state) {
      return ({
        queued: 'bg-secondary',
        progressing: 'bg-warning text-dark',
        ready: 'bg-info text-dark',
        completed: 'bg-success',
        cancelled: 'bg-danger',
      })[state] || 'bg-secondary';
    },
    stateIcon(state) {
      return ({
        queued: 'fa-solid fa-hourglass-half',
        progressing: 'fa-solid fa-fire-burner',
        ready: 'fa-solid fa-bell-concierge',
        completed: 'fa-solid fa-check-double',
        cancelled: 'fa-solid fa-ban',
      })[state] || 'fa-solid fa-circle-question';
    },
    stateLabel(state) {
      return ({
        queued: 'Sent to kitchen',
        progressing: 'Cooking',
        ready: 'Ready to serve',
        completed: 'Served',
        cancelled: 'Cancelled',
      })[state] || state;
    },
    /**
     * Running index across all groups — fed into the per-card
     * --enter-delay variable so the stagger continues smoothly when
     * crossing a group boundary rather than restarting at zero.
     */
    staggerIndex(groupIdx, itemIdx) {
      let n = 0;
      for (let i = 0; i < groupIdx; i++) {
        n += this.groupedItems[i].items.length;
      }
      return n + itemIdx;
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
/* Warm, brand-tinted shimmer shared across the customer-facing service
   tabs. A soft peach base with a glossy highlight sweep — reads as
   "the restaurant is plating your view" rather than the cold grey
   placeholder used on the staff/back-office side. */
.skeleton-shimmer {
  background: linear-gradient(100deg,
    #f4e6d8 0%,
    #fcefe4 42%,
    #fff9f3 50%,
    #fcefe4 58%,
    #f4e6d8 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.4s ease-in-out infinite;
  display: block;
}
/* Lighter shimmer for placeholders sitting on the orange theme header. */
.skeleton-shimmer-light {
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.35) 50%,
    rgba(255, 255, 255, 0.15) 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.4s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/*
  Cross-fade kept short (~120ms each side) so the real data isn't
  visibly held back by the leave animation when `mode="out-in"` plays
  the two halves sequentially.
*/
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.12s ease;
}
.fade-swap-enter-from,
.fade-swap-leave-to {
  opacity: 0;
}

/*
  Staggered card entrance — each card sets its own --enter-delay
  inline. Tight stagger (~25ms apart, 0.2s per card) keeps the
  ripple-in feel without making the list visibly slow to appear.
*/
.list-stagger-enter-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
  transition-delay: var(--enter-delay, 0ms);
}
.list-stagger-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
</style>
